// SEO delle pagine del negozio: titolo, descrizione, canonical per pagina e dati strutturati
// (schema.org Store, BreadcrumbList, CollectionPage). La scheda prodotto gestisce i propri.
import { optimizeImageUrl } from '../utils/imageUrl';

const PAGE_TITLES = {
  prodotti: 'Le creazioni',
  collezioni: 'Collezioni',
  'su-misura': 'Su misura',
  laboratorio: 'Il laboratorio',
  portfolio: 'Portfolio',
  recensioni: 'Recensioni',
  contatti: 'Contatti',
  'spedizioni-resi': 'Spedizioni e resi',
};

const productImage = (p) => {
  if (!p) return null;
  const i = p.customization_options?.main_image_index || 0;
  return p.images?.[i] || p.images?.[0] || null;
};

const clip = (t, n = 160) => {
  const s = (t || '').replace(/\s+/g, ' ').trim();
  return s.length <= n ? s : `${s.slice(0, n - 1).trim()}…`;
};

// null = pagina non gestita qui (scheda prodotto)
export function shopPageSeo(ctx, pathname) {
  const { crafter, slug, name, branding, collections, reviewStats, products } = ctx;
  const base = `/artigiano/${crafter.slug || slug}`;
  const origin = window.location.origin;
  const rest = pathname.replace(new RegExp(`^/artigiano/${slug}/?`, 'i'), '').replace(/\/$/, '');
  if (rest.startsWith('prodotto/')) return null;

  const about = clip(crafter.storefront_motto || crafter.bio) || `Le creazioni artigianali Made in Italy di ${name}.`;
  const image = crafter.cover_image || branding.logo_url || productImage(products[0]);
  const crumbs = [{ name, path: base }];
  let title = `${name} · Bottega artigiana Made in Italy${crafter.city ? ` · ${crafter.city}` : ''}`;
  let description = about;
  let canonicalPath = base;
  let pageImage = image;
  let page = null;

  const [seg, sub] = rest.split('/');
  if (seg === 'collezioni' && sub) {
    const col = collections.find((c) => c.slug === sub);
    if (col) {
      title = `${col.title} · ${name}`;
      description = clip(col.description) || `La collezione «${col.title}» di ${name}, fatta a mano in Italia.`;
      canonicalPath = `${base}/collezioni/${col.slug}`;
      pageImage = productImage(col.cover) || image;
      crumbs.push({ name: 'Collezioni', path: `${base}/collezioni` }, { name: col.title, path: canonicalPath });
      page = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: col.title,
        description,
        url: `${origin}${canonicalPath}`,
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: col.products.slice(0, 30).map((p, i) => ({
            '@type': 'ListItem', position: i + 1, url: `${origin}/prodotto/${p.slug || p.id}`, name: p.title,
          })),
        },
      };
    }
  } else if (seg && PAGE_TITLES[seg]) {
    title = `${PAGE_TITLES[seg]} · ${name}`;
    canonicalPath = `${base}/${seg}`;
    crumbs.push({ name: PAGE_TITLES[seg], path: canonicalPath });
  }

  const store = !seg ? {
    '@context': 'https://schema.org',
    '@type': 'Store',
    name,
    url: `${origin}${base}`,
    description: about,
    ...(image ? { image } : {}),
    ...(branding.logo_url ? { logo: branding.logo_url } : {}),
    ...(crafter.city ? {
      address: {
        '@type': 'PostalAddress',
        addressLocality: crafter.city,
        ...(crafter.region ? { addressRegion: crafter.region } : {}),
        addressCountry: 'IT',
      },
    } : {}),
    ...(reviewStats?.reviewCount ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: Number(reviewStats.averageRating.toFixed(1)),
        reviewCount: reviewStats.reviewCount,
      },
    } : {}),
    ...(crafter.social_links ? { sameAs: Object.values(crafter.social_links).filter((v) => v && /^https?:/.test(v)) } : {}),
  } : null;

  const breadcrumb = crumbs.length > 1 ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, item: `${origin}${c.path}` })),
  } : null;

  return {
    title,
    description,
    canonicalPath,
    image: pageImage ? optimizeImageUrl(pageImage, 1200) : null,
    jsonLd: { store, breadcrumb, page },
  };
}
